import { useEffect } from 'react';

import { isAuthEnabled } from '#/config/auth';
import {
  exitToLogin,
  SESSION_EXIT_REASON,
} from '#/utils/auth/sessionExit';
import { getExpiresAt } from '#/utils/auth/tokenStorage';

const CHECK_INTERVAL_MS = 5 * 60 * 1000;

/**
 * 解析 localStorage `expires_at`（API 例：`YYYY-MM-DD HH:mm:ss` 或 ISO）。
 * @returns {number|null} epoch ms；無法解析則 null
 */
function parseExpiresAtMs(expiresAt) {
  if (expiresAt == null || expiresAt === '') {
    return null;
  }

  const raw = String(expiresAt).trim();
  const normalized = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}/.test(raw)
    ? raw.replace(' ', 'T')
    : raw;
  const ms = Date.parse(normalized);
  return Number.isFinite(ms) ? ms : null;
}

function checkTokenExpiry() {
  if (!isAuthEnabled) {
    return;
  }

  const expiresAt = getExpiresAt();
  if (!expiresAt) {
    return;
  }

  const expiresAtMs = parseExpiresAtMs(expiresAt);
  if (expiresAtMs == null) {
    return;
  }

  if (Date.now() >= expiresAtMs) {
    exitToLogin({ reason: SESSION_EXIT_REASON.TOKEN_EXPIRED });
  }
}

/**
 * Auth on：AdminLayout 掛載即查＋每 5 分鐘；分頁可見／視窗 focus 立刻再查。
 * Auth off：整段 no-op。無 `expires_at`／無法解析 → **不** proactive 登出。
 */
function useTokenExpiryCheck() {
  useEffect(() => {
    if (!isAuthEnabled) {
      return undefined;
    }

    function handleVisibilityChange() {
      if (document.visibilityState === 'visible') {
        checkTokenExpiry();
      }
    }

    function handleWindowFocus() {
      checkTokenExpiry();
    }

    checkTokenExpiry();
    const timerId = window.setInterval(checkTokenExpiry, CHECK_INTERVAL_MS);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleWindowFocus);

    return () => {
      window.clearInterval(timerId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleWindowFocus);
    };
  }, []);
}

export default useTokenExpiryCheck;
